<template>
  <section v-if="video && show" class="page-video" :class="{ 'page-video--inline': inline }" aria-labelledby="page-video-title">
    <div :class="inline ? 'py-2' : 'mx-auto max-w-4xl px-4 py-12 sm:px-6 md:py-16'">
      <h2 id="page-video-title" class="page-video-title">{{ video.title }}</h2>
      <p class="page-video-meta">{{ video.channel }} on YouTube · {{ minutes }} min</p>

      <div class="page-video-frame">
        <template v-if="!playing">
          <img
            :src="`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`"
            :alt="`Video thumbnail: ${video.title}`"
            width="480"
            height="360"
            loading="lazy"
            decoding="async"
          />
          <button type="button" class="page-video-play" :aria-label="`Play video: ${video.title}`" @click="playing = true">
            <span aria-hidden="true">▶</span>
          </button>
        </template>
        <iframe
          v-else
          :src="`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`"
          :title="video.title"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowfullscreen
        ></iframe>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { stripLocaleFromPath } from "~/data/locales";
import { pageVideos } from "~/data/page-videos";

const props = defineProps({ inline: { type: Boolean, default: false } });
const route = useRoute();
const playing = ref(false);

const video = computed(() => {
  const path = stripLocaleFromPath(route.path).replace(/\/+$/, "") || "/";
  return pageVideos[path] ?? null;
});

// Blog posts place the video inside the article (BlogDesignEditorial); the layout copy skips them.
const isBlogPost = computed(() => /^\/blog\/[^/]+$/.test(stripLocaleFromPath(route.path).replace(/\/+$/, "")));
const show = computed(() => props.inline || !isBlogPost.value);

watch(() => route.path, () => (playing.value = false));

const minutes = computed(() => {
  const m = video.value?.duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!m) return 0;
  return Math.max(1, Math.round(Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0) + Number(m[3] ?? 0) / 60));
});

useHead(() =>
  video.value && show.value
    ? {
        script: [
          {
            key: "page-video-schema",
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              name: video.value.title,
              description: video.value.description,
              thumbnailUrl: [
                `https://i.ytimg.com/vi/${video.value.id}/hqdefault.jpg`,
                `https://i.ytimg.com/vi/${video.value.id}/mqdefault.jpg`,
              ],
              uploadDate: video.value.uploadDate,
              duration: video.value.duration,
              inLanguage: "en",
              embedUrl: `https://www.youtube-nocookie.com/embed/${video.value.id}`,
              url: `https://www.youtube.com/watch?v=${video.value.id}`,
              publisher: { "@type": "Organization", name: video.value.channel },
            }),
          },
        ],
      }
    : {},
);
</script>

<style scoped>
.page-video {
  background: #f8fafc;
  border-top: 1px solid rgb(148 163 184 / 0.2);
}
.dark .page-video {
  background: #020617;
  border-top-color: rgb(51 65 85 / 0.6);
}
.page-video-title {
  margin-top: 0;
  color: #0f172a;
  font-size: clamp(1.35rem, 3vw, 1.9rem);
  font-weight: 800;
  line-height: 1.2;
}
.dark .page-video-title {
  color: #f8fafc;
}
.page-video-meta {
  margin-top: 0.4rem;
  color: #64748b;
  font-size: 0.9rem;
}
.dark .page-video-meta {
  color: #94a3b8;
}
.page-video-frame {
  position: relative;
  overflow: hidden;
  margin-top: 1.5rem;
  border: 1px solid rgb(148 163 184 / 0.35);
  border-radius: 10px;
  background: #020617;
  box-shadow: 0 30px 80px rgb(2 6 23 / 0.16);
  aspect-ratio: 16 / 9;
}
.page-video-frame img,
.page-video-frame iframe {
  display: block;
  height: 100%;
  width: 100%;
  border: 0;
  object-fit: cover;
}
.page-video-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(2 6 23 / 0.25);
  cursor: pointer;
  transition: background-color 160ms ease;
}
.page-video-play:hover {
  background: rgb(2 6 23 / 0.4);
}
.page-video-play span {
  display: flex;
  height: 4.25rem;
  width: 4.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #0e7490;
  color: #fff;
  font-size: 1.5rem;
  padding-left: 0.35rem;
  box-shadow: 0 16px 44px rgb(2 6 23 / 0.4);
}
.page-video--inline,
.dark .page-video--inline {
  margin: 2rem 0;
  background: transparent;
  border-top: 0;
}
.page-video--inline .page-video-title {
  font-size: 1.25rem;
}
</style>
